import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when changing page
  useEffect(() => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const openMenu = (index) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(index);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 180);
  };

  const menuStructure = [
    { label: 'Accueil', path: '/' },
    {
      label: 'Prestations',
      dropdown: [
        {
          group: 'Nos prestations',
          items: [
            { label: 'Consultation nutritionnelle', path: '/prestations/consultation-nutritionnelle' },
            { label: "Séance d'hypnopraxie", path: '/prestations/hypnopraxie', badge: 'Nouveau' },
            { label: 'Toutes les consultations', path: '/consultations' }
          ]
        },
        {
          group: 'Nos spécialités',
          items: [
            { label: 'Nutrition hormonale', path: '/nutrition-hormonale' },
            { label: 'SOPK & alimentation', path: '/sopk-alimentation' },
            { label: 'Périménopause & métabolisme', path: '/premenopause-metabolisme' },
            { label: 'Résistance à l\'insuline', path: '/resistance-insuline-nutrition' },
            { label: 'Perte de poids hormonale', path: '/perte-poids-hormonale' },
            { label: 'Fertilité & nutrition', path: '/fertilite-nutrition-femme' },
            { label: 'Équilibre émotionnel', path: '/equilibre-emotionnel' },
            { label: 'Santé cardiovasculaire', path: '/sante-cardiovasculaire' },
            { label: 'Hypnopraxie® (méthode)', path: '/hypnopraxie-vevey-montreux' }
          ]
        }
      ]
    },
    { label: 'Consultations', path: '/consultations' },
    { label: 'Cabinets', path: '/cabinets' },
    { label: 'Le centre', path: '/le-centre' },
    { label: 'Équipe', path: '/equipe' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact', path: '/contact' }
  ];

  return (
    <header className={`header-nav ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container-wide" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Link to="/" className="logo-container">
          <img
            src="/images/logo.webp"
            alt="Nutri Centre Riviera"
            className="logo"
            style={{ height: '6rem' }}
          />
        </Link>

        <nav className="nav-menu" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          {menuStructure.map((item, index) => (
            item.dropdown ? (
              <div
                key={index}
                style={{ position: 'relative' }}
                onMouseEnter={() => openMenu(index)}
                onMouseLeave={scheduleClose}
              >
                <button
                  type="button"
                  onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                  aria-expanded={openDropdown === index}
                  aria-haspopup="true"
                  data-testid="header-prestations-trigger"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    padding: '0.5rem 0'
                  }}
                  className="nav-link-dropdown"
                >
                  {item.label}
                  <ChevronDown size={16} style={{
                    transition: 'transform 0.2s ease',
                    transform: openDropdown === index ? 'rotate(180deg)' : 'rotate(0)'
                  }} />
                </button>

                {/* Invisible bridge to prevent hover gap */}
                {openDropdown === index && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      height: '14px'
                    }}
                  />
                )}

                {openDropdown === index && (
                  <div
                    data-testid="header-prestations-menu"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 14px)',
                      left: 0,
                      background: 'white',
                      borderRadius: '0.85rem',
                      boxShadow: '0 18px 50px rgba(20, 45, 35, 0.14)',
                      padding: '1rem 0.5rem',
                      minWidth: '320px',
                      zIndex: 1000,
                      border: '1px solid var(--border-light)'
                    }}
                    onMouseEnter={() => openMenu(index)}
                    onMouseLeave={scheduleClose}
                  >
                    {item.dropdown.map((section, sIdx) => (
                      <div
                        key={sIdx}
                        style={{
                          paddingTop: sIdx === 0 ? 0 : '0.5rem',
                          marginTop: sIdx === 0 ? 0 : '0.5rem',
                          borderTop: sIdx === 0 ? 'none' : '1px solid var(--border-light)'
                        }}
                      >
                        <div style={{
                          padding: '0.35rem 1rem 0.5rem',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: 'var(--accent-primary)'
                        }}>
                          {section.group}
                        </div>
                        {section.items.map((subItem, subIndex) => {
                          const isActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subIndex}
                              to={subItem.path}
                              data-testid={`header-prestations-item-${subItem.path.replace(/\//g, '-')}`}
                              onClick={() => setOpenDropdown(null)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '0.75rem',
                                padding: '0.65rem 1rem',
                                color: isActive ? 'var(--accent-primary)' : 'var(--text-primary)',
                                fontWeight: isActive ? 600 : 500,
                                textDecoration: 'none',
                                fontSize: '0.92rem',
                                borderRadius: '0.5rem',
                                transition: 'all 0.18s ease'
                              }}
                              className="dropdown-item"
                            >
                              <span>{subItem.label}</span>
                              {subItem.badge && (
                                <span style={{
                                  background: 'var(--accent-gold)',
                                  color: '#1C2321',
                                  padding: '0.15rem 0.55rem',
                                  borderRadius: '999px',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  flexShrink: 0
                                }}>
                                  {subItem.badge}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={index}
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
              >
                {item.label}
              </Link>
            )
          ))}
        </nav>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexShrink: 0
        }} className="header-cta-stack">
          <a
            href="tel:+41767799302"
            data-testid="header-phone"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.65rem',
              padding: '0.6rem 1.15rem',
              borderRadius: '999px',
              border: '1.5px solid var(--accent-primary)',
              background: 'white',
              color: 'var(--accent-primary)',
              fontSize: '0.95rem',
              fontWeight: 700,
              textDecoration: 'none',
              transition: 'all 0.25s ease',
              whiteSpace: 'nowrap',
              letterSpacing: '0.01em'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--accent-primary)';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 18px rgba(61, 84, 70, 0.22)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = 'var(--accent-primary)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1.8rem',
              height: '1.8rem',
              borderRadius: '50%',
              background: 'var(--accent-primary-light, #E9EEE8)',
              color: 'var(--accent-primary)',
              flexShrink: 0
            }}>
              <Phone size={14} strokeWidth={2.5} />
            </span>
            076 779 93 02
          </a>
          <a
            href="https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ textDecoration: 'none' }}
            data-testid="header-rdv-btn"
          >
            Prendre rendez-vous
          </a>
        </div>

        <button 
          className="mobile-menu-btn"
          data-testid="header-burger"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div style={{
          position: 'absolute',
          top: '100px',
          left: 0,
          right: 0,
          background: 'var(--bg-overlay)',
          backdropFilter: 'blur(12px)',
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-light)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          {menuStructure.map((item, index) => (
            item.dropdown ? (
              <div key={index}>
                <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-primary)', fontSize: '1.05rem' }}>
                  {item.label}
                </div>
                {item.dropdown.map((section, sIdx) => (
                  <div key={sIdx} style={{ marginBottom: '0.75rem' }}>
                    <div style={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'var(--accent-primary)',
                      padding: '0.5rem 1rem 0.25rem'
                    }}>
                      {section.group}
                    </div>
                    {section.items.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        className="nav-link"
                        onClick={() => setIsMobileMenuOpen(false)}
                        style={{
                          fontSize: '0.95rem',
                          paddingLeft: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.75rem',
                          marginBottom: '0.35rem'
                        }}
                      >
                        <span>{subItem.label}</span>
                        {subItem.badge && (
                          <span style={{
                            background: 'var(--accent-gold)',
                            color: '#1C2321',
                            padding: '0.15rem 0.55rem',
                            borderRadius: '999px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase'
                          }}>
                            {subItem.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <Link
                key={index}
                to={item.path}
                className="nav-link"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ fontSize: '1.05rem' }}
              >
                {item.label}
              </Link>
            )
          ))}
          <a 
            href="https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary" 
            onClick={() => setIsMobileMenuOpen(false)}
            style={{ textDecoration: 'none' }}
          >
            Prendre rendez-vous
          </a>
        </div>
      )}

      <style>{`
        .nav-link-dropdown:hover {
          color: var(--accent-primary);
        }
        .dropdown-item:hover {
          background: var(--accent-primary-light);
          color: var(--accent-primary);
        }
      `}</style>
    </header>
  );
};

export default Header;
